import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './Sidebar.css';
import addProductIcon      from '../Assets/Product_Cart.svg';
import newArrivalsIcon     from '../Assets/new-arrival.png';
import bannerIcon          from '../Assets/banner_icon.png';
import faqIcon             from '../Assets/faq_icon.png';
import dashboardIcon       from '../Assets/dashboard_icon.png';
import categoryIcon        from '../Assets/category.png';
import designPatternIcon   from '../Assets/designpatterns.png';
import videosIcon          from '../Assets/videos.png';
import varietyOfAccessoriesIcon from '../Assets/variety.png';
import topSellersIcon      from '../Assets/topsellers.png';
import ordersIcon          from '../Assets/orders.png';
import usersIcon           from '../Assets/users.png';
import ideasIcon           from '../Assets/ideas.png';
import featureIcon         from '../Assets/new-features.png';
import promoIcon           from '../Assets/promo.png';
import ratingIcon          from '../Assets/rating.png';
import stockIcon           from '../Assets/stock.svg';

import { canAccess } from '../../utils/menuAccess';
import { backend_url } from '../../App';

// Menu visibility follows each user's Menu Access (set in User Management)
const role = () => sessionStorage.getItem("adminRole") || "";
const ROLE_LABELS = {
  "super-admin": "Super Admin", admin: "Admin", "admin-staff": "Admin Staff",
  staff: "Staff", "stock-staff": "Stock Staff",
};

const Svg = ({ children }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">{children}</svg>
);
const img = (src, alt) => <img src={src} alt={alt} />;

/* Sidebar layout — grouped by what the admin is doing. `key` is the
   Menu Access permission (User Management); items without it are hidden. */
const GROUPS = [
  {
    id: "overview", title: "Overview",
    items: [{ key: "dashboard", to: "/dashboard", label: "Dashboard", icon: img(dashboardIcon, "") }],
  },
  {
    id: "sales", title: "Sales",
    items: [
      { key: "orders", to: "/orders", label: "Orders", icon: img(ordersIcon, "") },
      { key: "customers", to: "/customers", label: "Customers", icon: img(usersIcon, "") },
      { key: "promo", to: "/promo-codes", label: "Promocode", icon: img(promoIcon, "") },
      { key: "gst-report", to: "/gst-report", label: "GST Report", icon: (
        <Svg><rect x="4" y="3" width="16" height="18" rx="2" fill="#fef3c7" stroke="#d97706" strokeWidth="1.8"/><path d="M8 8h8M8 12h8M8 16h5" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round"/></Svg>
      ) },
    ],
  },
  {
    id: "products", title: "Products",
    items: [
      { key: "products", to: "/listproduct", label: "Products", icon: img(addProductIcon, ""), also: ["/addproduct", "/editproduct"] },
      { key: "approvals", to: "/product-approvals", label: "Approvals", badge: "approvals", icon: (
        <Svg><rect x="4" y="3" width="16" height="18" rx="2" fill="#dcfce7" stroke="#16a34a" strokeWidth="1.6"/><path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="#16a34a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></Svg>
      ) },
    ],
  },
  {
    id: "inventory", title: "Inventory",
    items: [
      { key: "stock", to: "/stock", label: "Stock", icon: img(stockIcon, "") },
      { key: "saree-details", to: "/saree-details", label: "Saree Details", icon: (
        <Svg><path d="M4 4h16v4a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V4z" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.6"/><path d="M6 12v8M18 12v8M6 20h12" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 7h6" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round"/></Svg>
      ) },
      { key: "saree-barcodes", to: "/saree-barcodes", label: "Saree Barcodes", icon: (
        <Svg><rect x="3" y="4" width="18" height="16" rx="2" fill="#f5f3ff" stroke="#7c3aed" strokeWidth="1.6"/><path d="M7 8v8M10 8v8M13 8v8M17 8v8" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round"/></Svg>
      ) },
      { key: "excel-upload", to: "/excel-upload", label: "Excel Upload", icon: (
        <Svg><rect x="3" y="3" width="18" height="18" rx="3" fill="#dcfce7" stroke="#16a34a" strokeWidth="1.8"/><path d="M8 8h2v8H8zM11 8h5v3h-5zM11 13h5v3h-5z" fill="#16a34a"/></Svg>
      ) },
    ],
  },
  {
    id: "website", title: "Website",
    items: [
      { key: "homepage", to: "/homepage/website-menu", label: "Website Menu", icon: img(categoryIcon, "") },
      { key: "homepage", to: "/homepage/about-fabric", label: "About Our Fabric", icon: img(ideasIcon, "") },
      { key: "homepage", to: "/homepage/social-links", label: "Social Links", icon: img(featureIcon, "") },
      { key: "homepage", to: "/homepage/product-tabs", label: "Product Page Tabs", icon: img(faqIcon, "") },
      { key: "faq", to: "/frequently-asked-questions", label: "FAQ", icon: img(faqIcon, "") },
      { key: "ideas", to: "/explore-our-ideas", label: "Our Ideas", icon: img(ideasIcon, "") },
    ],
  },
  {
    id: "homepage", title: "Home Page",
    items: [
      { key: "homepage", to: "/homepage/landingbanners", label: "Landing Banners", icon: img(bannerIcon, "") },
      { key: "homepage", to: "/homepage/browsebycategory", label: "Category", icon: img(categoryIcon, "") },
      { key: "homepage", to: "/homepage/newarrivals", label: "New Arrivals", icon: img(newArrivalsIcon, "") },
      { key: "homepage", to: "/homepage/varietyofaccessories", label: "Variety of Accessories", icon: img(varietyOfAccessoriesIcon, "") },
      { key: "homepage", to: "/homepage/topsellers-retrocollections", label: "Top Sellers / Retro", icon: img(topSellersIcon, "") },
      { key: "homepage", to: "/homepage/shopbydesignpatterns", label: "Design Patterns", icon: img(designPatternIcon, "") },
      { key: "homepage", to: "/homepage/browsebyvideos", label: "Videos", icon: img(videosIcon, "") },
      { key: "homepage", to: "/homepage/featured-in", label: "Featured In", icon: img(featureIcon, "") },
      { key: "homepage", to: "/homepage/reviews", label: "Reviews", icon: img(ratingIcon, "") },
      { key: "homepage", to: "/homepage/testimonials", label: "Testimonials", icon: img(ratingIcon, "") },
    ],
  },
  {
    id: "admin", title: "Admin",
    items: [
      { key: "users", to: "/user-management", label: "Users", icon: (
        <Svg><circle cx="9" cy="7" r="4" fill="#e9d5ff" stroke="#7c3aed" strokeWidth="1.8"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round"/><path d="M16 11h6M19 8v6" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round"/></Svg>
      ) },
      { key: "settings", to: "/settings", label: "Settings", icon: (
        <Svg><circle cx="12" cy="12" r="3" stroke="#7c3aed" strokeWidth="1.8"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></Svg>
      ) },
      { key: "audit-log", to: "/audit-log", label: "Audit Log", icon: (
        <Svg><path d="M9 2h6l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" fill="#ede9fe" stroke="#7c3aed" strokeWidth="1.6"/><path d="M9 12h6M9 16h6M9 8h2" stroke="#7c3aed" strokeWidth="1.6" strokeLinecap="round"/></Svg>
      ) },
    ],
  },
];

const COLLAPSED_KEY = "sidebarCollapsedGroups";
const readCollapsed = () => {
  try { return JSON.parse(localStorage.getItem(COLLAPSED_KEY)) || {}; } catch { return {}; }
};

const pathMatches = (pathname, to) => pathname === to || pathname.startsWith(to + "/");
const itemActive = (pathname, item) =>
  item.children
    ? item.children.some((c) => pathMatches(pathname, c.to))
    : [item.to, ...(item.also || [])].some((p) => pathMatches(pathname, p));

const Sidebar = () => {
  const { pathname } = useLocation();
  const adminName = sessionStorage.getItem("adminName") || "Admin";
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Badge: product changes waiting for approval (admins only); refreshes on
  // navigation and whenever the Approvals page approves/rejects something
  useEffect(() => {
    if (!canAccess("approvals")) return;
    const refresh = () =>
      fetch(`${backend_url}/api/product-requests/count`, {
        headers: { Authorization: `Bearer ${sessionStorage.getItem("adminToken")}` },
      })
        .then((r) => r.json())
        .then((d) => d.success && setPendingApprovals(d.count))
        .catch(() => {});
    refresh();
    window.addEventListener("approvals-changed", refresh);
    return () => window.removeEventListener("approvals-changed", refresh);
  }, [pathname]);

  const toggleGroup = (id) =>
    setCollapsed((c) => {
      const next = { ...c, [id]: !c[id] };
      try { localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });

  // Groups with only the items this user may open; filtered by the search box
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GROUPS.map((g) => ({
      ...g,
      items: g.items
        .filter((it) => canAccess(it.key))
        .map((it) => {
          if (!q || !it.children) return it;
          const kids = it.children.filter((c) => c.label.toLowerCase().includes(q));
          return it.label.toLowerCase().includes(q) ? it : kids.length ? { ...it, children: kids } : null;
        })
        .filter((it) => it && (!q || it.children || it.label.toLowerCase().includes(q) || g.title.toLowerCase().includes(q))),
    })).filter((g) => g.items.length);
  }, [query]);

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = "/admin/login";
  };

  const navCls = ({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`;
  const dropCls = ({ isActive }) => `dropdown-item ${isActive ? 'active' : ''}`;
  const searching = !!query.trim();

  return (
    <div className="sidebar">
      <div className="sidebar-user-top">
        <div className="sidebar-user-avatar">{adminName.charAt(0).toUpperCase()}</div>
        <div className="sidebar-user-info">
          <span className="sidebar-user-name">{adminName}</span>
          <span className="sidebar-user-role">{ROLE_LABELS[role()] || role() || "—"}</span>
        </div>
      </div>

      <div className="sidebar-search">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
        <input type="search" placeholder="Find a page…" value={query} aria-label="Find a page"
          onChange={(e) => setQuery(e.target.value)} />
      </div>

      <nav className="sidebar-scroll" aria-label="Admin menu">
        {groups.map((g) => {
          const hasActive = g.items.some((it) => itemActive(pathname, it));
          const isOpen = searching || hasActive || !collapsed[g.id];
          return (
            <div key={g.id} className={`sidebar-group ${isOpen ? "open" : ""}`}>
              {g.id !== "overview" && (
                <button type="button" className="sidebar-group-title" aria-expanded={isOpen}
                  onClick={() => !hasActive && !searching && toggleGroup(g.id)}
                  title={hasActive ? "You're in this section" : isOpen ? "Hide this section" : "Show this section"}>
                  <span>{g.title}</span>
                  {!isOpen && <span className="sidebar-group-count">{g.items.length}</span>}
                  <svg className="sidebar-group-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                </button>
              )}

              {isOpen && g.items.map((it) => it.children ? (
                <div key={it.label}>
                  <button type="button"
                    className={`sidebar-item sidebar-parent ${itemActive(pathname, it) ? "active" : ""}`}
                    aria-expanded={sectionsOpen || itemActive(pathname, it) || searching}
                    onClick={() => setSectionsOpen((o) => !o)}>
                    {it.icon}
                    <span>{it.label}</span>
                    <svg className={`arrow-icon ${sectionsOpen || itemActive(pathname, it) || searching ? "open" : ""}`} width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 15L12 9L18 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </button>
                  {(sectionsOpen || itemActive(pathname, it) || searching) && (
                    <div className="dropdown-content">
                      {it.children.map((c) => (
                        <NavLink key={c.to} to={c.to} className={dropCls}>
                          <img className="icon-img" src={c.icon} alt="" />
                          <p className="dropdown-text">{c.label}</p>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={it.to} to={it.to}
                  className={({ isActive }) => navCls({ isActive: isActive || itemActive(pathname, it) })}>
                  {it.icon}
                  <span>{it.label}</span>
                  {it.badge === "approvals" && pendingApprovals > 0 && (
                    <span className="sidebar-badge" title={`${pendingApprovals} waiting for approval`}>{pendingApprovals}</span>
                  )}
                </NavLink>
              ))}
            </div>
          );
        })}
        {groups.length === 0 && <p className="sidebar-empty">No page matches “{query}”.</p>}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-logout-btn" onClick={handleLogout}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M10 17l5-5-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Logout
        </button>
      </div>
    </div>
  );
};

export { GROUPS };
export default Sidebar;
